import { Performance as PerformanceIcon, ViewSidebar as ViewSidebarIcon } from '@tunasong/icons'
import type { BaseLayoutSpec } from '@tunasong/plugin-lib'
import { useLayout, useLayoutsExtended } from '@tunasong/plugin-lib'
import {
  FavoriteIcon,
  IconButton,
  LayoutSelector,
  Presence,
  TunaEntityBreadcrumbs,
  useDimensions,
  useNavigateToEntity,
  useSidebar,
  ToggleButton,
} from '@tunasong/ui-lib'
import type { MouseEvent } from 'react'
import CreateEntityButton from '../entity/create-entity-button.js'
import { Header } from '../header.js'
import type { ChromeProps } from './chrome.js'
import { EntityMenuButton } from './entity-menu.js'
import styles from './entity-header.module.css'

export interface EntityHeaderProps extends ChromeProps {
  ref?: React.Ref<HTMLDivElement>
}

export const EntityHeader = (props: EntityHeaderProps) => {
  const { entity, layoutName, showSidebar, ref } = props

  const { isTiny } = useDimensions()

  const layouts = useLayoutsExtended(entity)
  const navigateToEntity = useNavigateToEntity({ alwaysUseDefaultRouter: true })

  const navigateToLayout = (layout: BaseLayoutSpec) => navigateToEntity(entity, { layout: layout.name })

  const layout = useLayout({ entity, layoutName })

  const entityId = entity?.id

  const { setActiveTool, open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar()

  const handleToggleSidebar = (ev: MouseEvent) => {
    ev.preventDefault()
    if (!sidebarOpen) {
      setSidebarOpen(true)
      setActiveTool('Settings')
    } else {
      setSidebarOpen(false)
      setActiveTool(null)
    }
  }

  const container = layout?.container ? layout.container : {}

  return (
    <Header
      ref={ref}
      position="absolute"
      elevation={0}
      controls={[
        entity ? <FavoriteIcon entity={entity} key={-1} /> : null,
        entity ? <CreateEntityButton key={0} parent={entity} /> : null,
        layouts.length > 0 ? (
          <LayoutSelector
            // variant="outlined"
            selected={layout ?? undefined}
            layouts={layouts}
            key={211}
            onChange={navigateToLayout}
          />
        ) : null,
        entity && layouts.some(l => l.name === 'Performance') && layout?.name !== 'Performance' ? (
          <IconButton
            key={212}
            aria-label="Enter Performance mode"
            title="Perform"
            onClick={() => navigateToEntity(entity, { layout: 'Performance' })}
          >
            <PerformanceIcon />
          </IconButton>
        ) : null,

        <Presence key={10} entityId={entityId} className={styles.presence} />,
        showSidebar && isTiny ? (
          <ToggleButton
            size="small"
            value="sidebar"
            selected={sidebarOpen}
            key={11}
            onClick={handleToggleSidebar}
            aria-label="Toggle sidebar"
          >
            <ViewSidebarIcon color={sidebarOpen ? 'secondary' : undefined} />
          </ToggleButton>
        ) : null,
        <EntityMenuButton key={12} entity={entity} className={styles.menuButton} />,
      ].filter(Boolean)}
      showBackButton={container?.showBreadcrumbs !== false}
      breadcrumbs={
        container?.showBreadcrumbs !== false ? (
          <TunaEntityBreadcrumbs className={styles.breadcrumbs} entity={entity} showHome={true} />
        ) : undefined
      }
    />
  )
}
